import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { MachineRow, WorkOrderRow } from '../lib/types';
import { MachineForm } from '../components/forms';
import { Button, Card, Loading, Modal, PageHeader } from '../components/ui';
import { WorkOrderTable } from './WorkOrdersPage';
import { MachineLegacyCard } from './LegacyPages';

type Detail = MachineRow & { workOrders: WorkOrderRow[] };

export function MachinePage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const m = useQuery({ queryKey: ['machine', id], queryFn: () => api.get<Detail>(`/api/machines/${id}`) });
  const [editing, setEditing] = useState(false);
  if (m.isLoading || !m.data) return <Loading />;
  const d = m.data;
  return (
    <>
      <PageHeader title={`${d.make} ${d.model} · S/N ${d.serialNumber}`}>
        <Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button>
      </PageHeader>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Details">
          <dl className="space-y-1 text-sm">
            <div>Customer: {d.customer ? <Link className="text-blue-800 hover:underline" to={`/customers/${d.customer.id}`}>{d.customer.name}</Link> : '—'}</div>
            <div>EQ #: {d.equipmentNo ?? '—'}</div>
            <div>Controls: {d.controls ?? '—'}</div>
            <div>{d.isRentalFleet ? 'Rental fleet machine' : 'Customer machine'}</div>
            {!d.active && <div className="text-slate-500">Inactive</div>}
          </dl>
        </Card>
        <Card title="Service history" className="lg:col-span-2"><WorkOrderTable rows={d.workOrders.map((w) => ({ ...w, machine: d }))} /></Card>
        <MachineLegacyCard machineId={d.id} />
      </div>
      {editing && (
        <Modal title="Edit machine" onClose={() => setEditing(false)}>
          <MachineForm initial={d} onSaved={() => { setEditing(false); qc.invalidateQueries({ queryKey: ['machine', id] }); }} onCancel={() => setEditing(false)} />
        </Modal>
      )}
    </>
  );
}
